import { breakpointSizes, getBreakpointDown } from '@paywithextend/components'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import {
  selectPostingAvatarFromState,
  selectPostingAvatarTopLevelErrorFromState,
} from 'src/app/UpdateProfilePhoto/redux/updateProfilePhoto.selectors'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import Popup from 'src/components/Popup/Popup'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserAvatarType } from 'src/models/user.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { updateProfilePhotoUrl } from 'src/navigation/urls'
import { postUserAvatar } from 'src/redux/actions/data/users.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import styled, { css } from 'styled-components'

const ButtonContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  &:nth-child(n + 2) {
    margin-top: 20px;
  }
`

const UpdateProfilePhoto: React.FC = () => {
  const translate = useTranslate()

  const user = useSelector(selectUserListModelFromState)
  const postingPhoto = useSelector(selectPostingAvatarFromState)
  const postingPhotoError = useSelector(
    selectPostingAvatarTopLevelErrorFromState,
  )
  const location = useSelector(selectLocationFromState)
  const [showFileError, setShowFileError] = useState<boolean>(false)

  const dispatch = useFlyoutDispatch()

  useEffect((): void => {
    if (location.search) {
      dispatch(connectedReplace(updateProfilePhotoUrl()))
    }
  }, [location])

  const onGravatarButtonClick = (): void => {
    const form = new FormData()
    form.append('avatarType', UserAvatarType.Gravatar)
    dispatch(postUserAvatar(form))
  }

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
      setShowFileError && setShowFileError(false)
      acceptedFiles.forEach((file) => {
        const form = new FormData()
        form.append('file', file, file.name)
        form.append('avatarType', UserAvatarType.Upload)
        dispatch(postUserAvatar(form))
      })
      if (fileRejections.length > 0) {
        setShowFileError && setShowFileError(true)
      }
    },
    [],
  )
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
      'image/gif': ['.gif'],
      'image/bmp': ['.bmp'],
      'image/heic': ['.heic'],
    },
    maxSize: 10000000,
  })

  const onRemoveButtonClick = (): void => {
    const form = new FormData()
    form.append('avatarType', UserAvatarType.Default)
    dispatch(postUserAvatar(form))
  }

  return (
    <Popup
      css={css`
        width: 600px;
        padding: 60px;
        display: flex;
        justify-content: space-between;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          flex-direction: column;
          align-items: center;
          width: 100%;
        `)}
      `}
    >
      {user !== undefined ? (
        <Fragment>
          <div>
            <UserAvatar
              css={
                postingPhoto
                  ? css`
                      opacity: 0.5;
                    `
                  : ''
              }
              user={user}
              size={AvatarSizes.ExtraExtraHuge}
            />
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              flex-grow: 2;
              margin-left: 40px;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                margin-left: 0px;
                margin-top: 20px;
                width: 100%;
              `)}
            `}
          >
            <ButtonContainerDiv>
              <Button
                design={ButtonDesigns.designCtaInverse}
                type={ButtonTypes.button}
                text={translate('Use Gravatar photo')}
                onClick={onGravatarButtonClick}
              />
            </ButtonContainerDiv>
            <ButtonContainerDiv {...getRootProps()}>
              <input {...getInputProps()} />
              <Button
                css={css`
                  flex-grow: 2;
                `}
                design={ButtonDesigns.designCtaInverse}
                type={ButtonTypes.button}
                text={translate('Upload photo')}
              />
              {postingPhotoError && (
                <ErrorList
                  errors={[postingPhotoError]}
                  css={css`
                    padding-left: 0;
                  `}
                />
              )}
              {showFileError && (
                <ErrorList
                  errors={[
                    <React.Fragment key={1}>
                      {translate('Files cannot exceed 10MB')}
                    </React.Fragment>,
                  ]}
                  css={css`
                    padding-left: 0;
                  `}
                />
              )}
            </ButtonContainerDiv>
            <ButtonContainerDiv>
              <Button
                design={ButtonDesigns.designRed}
                type={ButtonTypes.button}
                text={translate('Remove')}
                onClick={onRemoveButtonClick}
              />
            </ButtonContainerDiv>
          </div>
        </Fragment>
      ) : (
        <NotFoundPopupContent />
      )}
    </Popup>
  )
}

export default UpdateProfilePhoto
